import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { brandApi } from '@/lib/api';
import { CreateBrandRequest, UpdateBrandRequest } from '@/types';
import { queryKeys } from './shared';

// Brands
export const useBrands = (workspaceId: string) =>
  useQuery({
    queryKey: queryKeys.brands(workspaceId),
    queryFn: () => brandApi.list(workspaceId),
    enabled: !!workspaceId,
  });

export const useCreateBrand = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBrandRequest) => brandApi.create(workspaceId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands(workspaceId) });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};

export const useUpdateBrand = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, data }: { brandId: string; data: UpdateBrandRequest }) =>
      brandApi.update(workspaceId, brandId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands(workspaceId) });
    },
  });
};

/** Multipart logo upload — POST /brands/:id/logo?variant=dark|light */
export const useUploadBrandLogo = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      brandId,
      file,
      variant,
    }: {
      brandId: string;
      file: File;
      variant: 'dark' | 'light';
    }) => brandApi.uploadLogo(workspaceId, brandId, file, variant),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands(workspaceId) });
    },
  });
};

export const useDeleteBrand = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) => brandApi.delete(workspaceId, brandId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands(workspaceId) });
      qc.invalidateQueries({ queryKey: queryKeys.stats(workspaceId) });
    },
  });
};
